import { IMutation, MutationContext, IAppStorage } from '@aesop-fables/scrinium';
import { ApiKeys } from '../../../api/apis/ApiKeys';
import {
  RevShareApi,
  AddRevSharePayload,
  PatchRevSharePayload,
} from '../../../api/apis/RevShareApi';
import { AdminTierTypeEnum } from '../../../services/drillDown';
import { RevShareCompartments, revShareStorageKey } from '..';
import { RevShareFormValues } from '../helpers/revShareSettingsUtils';

export async function reloadRevShareCache(
  storage: IAppStorage,
  adminTier: AdminTierTypeEnum,
): Promise<void> {
  const revShareCache = storage.retrieve<RevShareCompartments>(revShareStorageKey);

  switch (adminTier) {
    case AdminTierTypeEnum.Organization:
      await revShareCache.reload('orgRevShareSettings');
      break;
    case AdminTierTypeEnum.Team:
      await revShareCache.reload('teamRevShareSettings');
      break;
    case AdminTierTypeEnum.Group:
      await revShareCache.reload('groupRevShareSettings');
      break;
  }
}

export function resetRevShareCache(storage: IAppStorage, adminTier: AdminTierTypeEnum): void {
  const revShareCache = storage.retrieve<RevShareCompartments>(revShareStorageKey);

  switch (adminTier) {
    case AdminTierTypeEnum.Organization:
      revShareCache.reset('orgRevShareSettings');
      break;
    case AdminTierTypeEnum.Team:
      revShareCache.reset('teamRevShareSettings');
      break;
    case AdminTierTypeEnum.Group:
      revShareCache.reset('groupRevShareSettings');
      break;
  }
}

function buildRevSharePayload(formValues: RevShareFormValues): PatchRevSharePayload {
  const basePayload = {
    pricebookId: formValues.pricebookId,
    effectiveTimestamp: formValues.effectiveTimestamp ?? undefined,
    expireTimestamp:
      formValues.setEndDate === 'Yes' ? (formValues.expireTimestamp ?? undefined) : undefined,
  };

  if (formValues.revShareSetting === 'No fee share') {
    return {
      ...basePayload,
      baseAmount: 0,
      revSharePct: 0,
      fixedAmount: 0,
      revShareDorp: 'D',
    };
  }

  if (formValues.revShareMethod === '% of sale') {
    if (formValues.baseAmount === undefined || formValues.revSharePct === undefined) {
      throw new Error('Base amount and percentage are required for % of sale method');
    }
    return {
      ...basePayload,
      baseAmount: formValues.baseAmount,
      revSharePct: formValues.revSharePct,
      fixedAmount: 0,
      revShareDorp: 'P',
    };
  }

  if (formValues.revShareMethod === 'Fixed $ amt') {
    if (formValues.fixedAmount === undefined) {
      throw new Error('Fixed amount is required for Fixed $ amt method');
    }
    return {
      ...basePayload,
      baseAmount: 0,
      revSharePct: 0,
      fixedAmount: formValues.fixedAmount,
      revShareDorp: 'D',
    };
  }

  throw new Error('Invalid rev share configuration - method not selected');
}

export interface SaveRevenueShareEntryData {
  formValues: RevShareFormValues;
  adminTier: AdminTierTypeEnum;
}

export class SaveRevenueShareEntry implements IMutation<SaveRevenueShareEntryData> {
  async execute(context: MutationContext<SaveRevenueShareEntryData>): Promise<void> {
    const { data, storage, container } = context;
    const { formValues, adminTier } = data;
    const revShareApi = container.get<RevShareApi>(ApiKeys.RevShare);

    if (formValues.existingEntryId) {
      const patchPayload: PatchRevSharePayload = {
        ...buildRevSharePayload(formValues),
        clearExpireTimestamp: formValues.setEndDate === 'No' ? true : undefined,
      };
      await revShareApi.patchRevenueShare(formValues.existingEntryId, patchPayload);
    } else if (formValues.revShareSetting === 'No fee share') {
      await revShareApi.addDisabledRevShare(
        formValues.pricebookId,
        formValues.effectiveTimestamp ?? undefined,
        formValues.setEndDate === 'Yes' ? (formValues.expireTimestamp ?? undefined) : undefined,
      );
    } else {
      const payload: AddRevSharePayload = { ...buildRevSharePayload(formValues), tier: adminTier };
      await revShareApi.addRevenueShare(payload);
    }

    await reloadRevShareCache(storage, adminTier);
  }
}

export interface DeleteRevenueShareEntryData {
  entryId: number;
  adminTier: AdminTierTypeEnum;
}

export class DeleteRevenueShareEntry implements IMutation<DeleteRevenueShareEntryData> {
  async execute(context: MutationContext<DeleteRevenueShareEntryData>): Promise<void> {
    const { data, storage, container } = context;
    const revShareApi = container.get<RevShareApi>(ApiKeys.RevShare);

    await revShareApi.deleteRevenueShare(data.entryId);
    await reloadRevShareCache(storage, data.adminTier);
  }
}

export interface RevertToInheritedData {
  pricebookId: number;
  adminTier: AdminTierTypeEnum;
}

export class RevertToInherited implements IMutation<RevertToInheritedData> {
  async execute(context: MutationContext<RevertToInheritedData>): Promise<void> {
    const { data, storage, container } = context;
    const revShareApi = container.get<RevShareApi>(ApiKeys.RevShare);

    await revShareApi.deleteAllByPricebook(data.pricebookId);
    await reloadRevShareCache(storage, data.adminTier);
  }
}

export interface OverrideRevShareData {
  pricebookId: number;
  adminTier: AdminTierTypeEnum;
}

export class OverrideRevShare implements IMutation<OverrideRevShareData> {
  async execute(context: MutationContext<OverrideRevShareData>): Promise<void> {
    const { data, storage, container } = context;
    const revShareApi = container.get<RevShareApi>(ApiKeys.RevShare);

    await revShareApi.overrideRevenueShare(data.pricebookId);
    await reloadRevShareCache(storage, data.adminTier);
  }
}
